import { GraphDefinition } from "@/graph/GraphDefinition";
import { ApiBase } from "../runtime/ApiService";
import { SessionState } from "@/server/SessionState";
import { logger } from "@/logging";
import { config } from "@/config";


export class ServerApi extends ApiBase {


       
  constructor(sessionId, webSocketClass) {
    super(sessionId)

    this.WebSocketClass = webSocketClass
  }



  async waitUntilReady(maxWaitMs = 10000, intervalMs = 200): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < maxWaitMs) {
      try {
        await this.get('health');
        return;
      } catch {
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }
    throw new Error('Server not ready after ' + maxWaitMs + 'ms');
  }

  /**
   * Post a position update to server
   */
  async postPosition(nodeId: string, x: number, y: number): Promise<void> {
    await this.post(`position`, { nodeId, x, y })
  }

  /**
   * Post a viewport update to server
   */
  async postViewport(x: number, y: number, zoom: number): Promise<void> {
    await this.post(`viewport`, { x, y, zoom })
  }

  /**
   * Post unified session state (positions, viewport)
   */
  async postSession(data: {
    positions?: Record<string, { x: number; y: number }>;
    viewport?: { x: number; y: number; zoom: number };
  }): Promise<void> {

    await this.post(`session`, data)

  }

  /**
   * Post a graph definition to the server
   */
  async postDefinition(definition: GraphDefinition): Promise<void> {
    await this.post(`postDefinition`, { definition })
  }

  /**
   * Fetch a library definition from server
   */
  async getLibraryDefinition(definitionId: string): Promise<GraphDefinition> {
    return await this.get(`definition/${encodeURIComponent(definitionId)}`)
  }

  /**
   * Fetch all library definitions from server in a single request
   */
  async getAllLibraryDefinitions(): Promise<Record<string, GraphDefinition>> {

    return await this.get(`library/definitions`)

  }

  /**
 * Fetch current session state from server
 */
  async getSession(): Promise<SessionState> {
    return await this.get(`session`)
  }

  async getProjects(): Promise<string[]> {
    const data = await this.get<{ projects: string[] }>('library/projects');
    return data.projects;
  }

  /**
   * Clear the session - reset to empty root
   */
  async clear(): Promise<void> {
    await this.post('clear', {});
  }

  // ─────────────────────────────────────────────────────────────
  // WebSocket
  // ─────────────────────────────────────────────────────────────



    private WebSocketClass: typeof WebSocket;
  
    private ws: WebSocket | null = null;

  /**
   * Connect to server WebSocket to receive state updates
   */
  connectWebSocket(onStateUpdate): void {
    if (this.ws || !this.WebSocketClass) return;

    try {
      this.ws = new this.WebSocketClass(`${config.wsUrl}/${this.sessionId}`);

      this.ws.onopen = () => {
        logger.info('ServerSync', 'WebSocket connected');
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const raw = typeof event.data === 'string' ? event.data : event.data.toString();
          const data = JSON.parse(raw);
          if (data.type === 'graphState' && data.state) {
            onStateUpdate(data.state);
          }
        } catch (err) {
          const preview = typeof event.data === 'string'
            ? event.data.slice(0, 100)
            : String(event.data).slice(0, 100);
          logger.error('ServerSync', `Failed to parse WebSocket message: ${err instanceof Error ? err.message : String(err)}. Preview: ${preview}`);
        }
      };

      this.ws.onclose = () => {
        logger.info('ServerSync', 'WebSocket disconnected');
        this.ws = null;
      };

      this.ws.onerror = (err) => {
        logger.error('ServerSync', 'WebSocket error', err);
      };
    } catch (err) {
      logger.error('ServerSync', 'Failed to connect WebSocket', err);
    }
  

  }
    /**
   * Disconnect WebSocket
   */
  disconnectWebSocket(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  dispose() {
    this.disconnectWebSocket();
  }

}

